import { useEffect, useState } from "react";
import { useSessionContext, useSpinnerContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import { Button, PaymentMethodCard, NoPaymentMethodCard } from "@common";
import { useNavigate, useLocation } from "react-router-dom";
import type { Account } from "@app-types";

const ResolveDefaultPaymentMethods = () => {
    const {
        accounts,
        selectedAccount,
        setSession,
        changeSelectedResident
    } = useSessionContext();
    const { setSpinnerShow } = useSpinnerContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [handled, setHandled] = useState<number[]>([]);

    // Get the account from location state (the account that was just updated).
    // This ensures the account remains the same even if you click "Add Payment
    // Method" and then navigate back.
    const updatedAccountId = location.state?.account?.idAccount;

    // make sure we start with most up to date version of session
    // as we may have just updated the default payment method
    useEffect(() => {
        const fetchSession = async () => {
            setSpinnerShow(true);
            try {
                const session = await sunboundPayProxy.refreshSession();
                setSession(session);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        };
        fetchSession();
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (!updatedAccountId) {
            // If no account in location state, assume the resident we're
            // navigated under.  This is a failsafe for cases such as direct
            // navigation to this page.
            navigate(location.pathname, {
                replace: true,
                state: { ...location.state, account: selectedAccount }
            });
        }
    }, []);

    if (!accounts || accounts.length === 0) return null;
    if (!updatedAccountId) return null;

    const updatedAccount = accounts.find(a => a.idAccount === updatedAccountId);
    const otherAccounts = accounts.filter(a => a.idAccount !== updatedAccountId)
        .filter(a => !a.paymentPreferences.defaultPaymentMethod || a.paymentPreferences.defaultPaymentMethod.disabledAt)
        .filter(a => !handled.includes(a.idAccount));

    // account to resolve is just pulled off top of remaining unresolved session.accounts
    const nextAccount = otherAccounts[0];

    const handleNext = () => {
        if (!nextAccount) return;
        if (otherAccounts.length > 1) {
            setHandled(prev => [...prev, nextAccount.idAccount]);
        } else {
            navigate(`/`);
        }
    };

    const handleMakeDefault = async () => {
        if (!nextAccount || !updatedAccount) return;
        const {idAccount} = nextAccount;
        const idPaymentMethod = updatedAccount.paymentPreferences.defaultPaymentMethod?.idPaymentMethod;
        if (!idAccount || !idPaymentMethod) {
            console.error(`Invalid account or payment method`);
            return;
        }

        setSpinnerShow(true);
        try {
            await sunboundPayProxy.upsertAccountDefaultPaymentMethod(idAccount, idPaymentMethod);
            const session = await sunboundPayProxy.refreshSession();
            setSession(session);
            handleNext();
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const handleAddPaymentMethod = (idAccount: number) => {
        changeSelectedResident(idAccount, navigate, `/payment-method-settings`);
    };

    return (
        <div className="flex flex-col w-full">
            {/* Header */}
            <div className="border-b border-neutral-100 pb-6 mb-8">
                <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">
                    Payment Method Update
                </h1>
            </div>

            {!updatedAccount || !nextAccount ? (
                /* Empty State */
                <div className="flex flex-col gap-8">
                    <p className="text-base leading-6 text-neutral-800 tracking-[0.16px]">
                        Nothing to do here.
                    </p>
                    <Button
                        variant="primary"
                        size="md"
                        className="w-fit"
                        onClick={() => navigate(`/`)}
                    >
                        Back to Dashboard
                    </Button>
                </div>
            ) : (
                <>
                    {/* Instructional Text */}
                    <p className="text-sm leading-5 md:text-base md:leading-6 text-neutral-800 tracking-[0.14px] md:tracking-[0.16px] mb-8">
                        Make sure your Autopay settings are set up accurately.
                    </p>

                    {/* Account Cards */}
                    <div className="flex flex-col gap-4 mb-8">
                        {/* Updated Account - Show the account that was just updated */}
                        <AccountDisplayCard account={updatedAccount} />

                        {/* Next Account - Show the account that needs a default */}
                        <AccountDisplayCard
                            account={nextAccount}
                            onAddPaymentMethod={() => handleAddPaymentMethod(nextAccount.idAccount)}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col md:flex-row gap-4 w-full">
                        <Button
                            variant="primary"
                            size="lg"
                            className="order-1 md:order-2 flex-1"
                            onClick={handleMakeDefault}
                        >
                            Make Default
                        </Button>
                        <Button
                            variant="secondary"
                            size="lg"
                            className="order-2 md:order-1 flex-1"
                            onClick={handleNext}
                        >
                            Skip for Now
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

interface AccountDisplayCardProps {
    account: Account;
    onAddPaymentMethod?: () => void;
}

const AccountDisplayCard: React.FC<AccountDisplayCardProps> = ({
    account,
    onAddPaymentMethod
}) => {
    const { residentName, paymentPreferences } = account;
    const currentDefaultPM = paymentPreferences.defaultPaymentMethod;

    return (
        <div className="bg-white border border-neutral-200 rounded-xl p-4">
            {/* Account Name */}
            <h3 className="text-base leading-6 font-medium text-neutral-950 tracking-[0.16px] mb-4">
                {residentName}
            </h3>

            {/* Payment Method Display */}
            {currentDefaultPM ? (
                <PaymentMethodCard
                    paymentMethod={currentDefaultPM}
                    isDefault={true}
                />
            ) : (
                <NoPaymentMethodCard
                    onAddPaymentMethod={onAddPaymentMethod}
                />
            )}
        </div>
    );
};

export default ResolveDefaultPaymentMethods;
